import { SKILLS, type SkillId } from './catalog.ts';
import type { DoctrineId, GameState } from './types.ts';

export const DOCTRINE_UNLOCK_ASCENSIONS = 3;

export interface DoctrineDefinition {
  id: DoctrineId;
  icon: string;
  name: string;
  description: string;
  advantage: string;
  drawback: string;
  needsFocusedSkill?: boolean;
}

export const DOCTRINES: readonly DoctrineDefinition[] = [
  { id: 'scholar', icon: '📚', name: "Scholar's Vow", description: 'Push levels and milestone unlocks at the cost of materials.', advantage: '+35% skill XP', drawback: '-15% resource production' },
  { id: 'architect', icon: '🏛️', name: "Architect's Covenant", description: 'Build a stronger Hall network while accepting weaker trade.', advantage: '-25% Hall costs · +10% resources', drawback: '-15% sale value' },
  { id: 'merchant', icon: '⚖️', name: "Merchant's Oath", description: 'Turn the realm into a Tool-funded gold economy.', advantage: '+40% sale value · -20% Tool costs', drawback: '-10% skill XP' },
  { id: 'specialist', icon: '🎯', name: 'The Singular Path', description: 'Name one skill as this run’s focus and accept slower side paths.', advantage: '+50% XP and resources for the focused skill', drawback: '-20% XP and resources for every other skill', needsFocusedSkill: true },
  { id: 'wayfarer', icon: '🌙', name: "Wayfarer's Pact", description: 'Shape a run around long absences rather than active speed.', advantage: '+4 offline hours · +15% offline efficiency', drawback: '-15% XP and resources while active' }
];

export interface DoctrineActionResult {
  ok: boolean;
  state: GameState;
  reason?: string;
}

export function chooseDoctrine(state: GameState, doctrineId: DoctrineId, focusedSkill?: SkillId, now = Date.now()): DoctrineActionResult {
  const doctrine = DOCTRINES.find((item) => item.id === doctrineId);
  if (!doctrine) return { ok: false, state, reason: 'Unknown Doctrine.' };
  if (state.prestige.ascensions < DOCTRINE_UNLOCK_ASCENSIONS) return { ok: false, state, reason: `Requires ${DOCTRINE_UNLOCK_ASCENSIONS} Ascensions.` };
  if (state.run.doctrine) return { ok: false, state, reason: 'A Doctrine has already been sworn for this run.' };
  if (doctrine.needsFocusedSkill && (!focusedSkill || !SKILLS.includes(focusedSkill))) return { ok: false, state, reason: 'Choose one focused skill.' };
  return {
    ok: true,
    state: {
      ...state,
      updatedAt: now,
      revision: state.revision + 1,
      run: { ...state.run, doctrine: { id: doctrineId, focusedSkill: doctrine.needsFocusedSkill ? focusedSkill : undefined, chosenAt: now } }
    }
  };
}

export function doctrineXpMultiplier(state: GameState, skill: SkillId, mode: 'active' | 'offline' = 'active'): number {
  const doctrine = state.run.doctrine;
  if (!doctrine) return 1;
  if (doctrine.id === 'scholar') return 1.35;
  if (doctrine.id === 'merchant') return 0.9;
  if (doctrine.id === 'specialist') return doctrine.focusedSkill === skill ? 1.5 : 0.8;
  if (doctrine.id === 'wayfarer') return mode === 'active' ? 0.85 : 1;
  return 1;
}

export function doctrineProductionMultiplier(state: GameState, skill: SkillId, mode: 'active' | 'offline' = 'active'): number {
  const doctrine = state.run.doctrine;
  if (!doctrine) return 1;
  if (doctrine.id === 'scholar') return 0.85;
  if (doctrine.id === 'architect') return 1.1;
  if (doctrine.id === 'specialist') return doctrine.focusedSkill === skill ? 1.5 : 0.8;
  if (doctrine.id === 'wayfarer') return mode === 'active' ? 0.85 : 1;
  return 1;
}

export function doctrineHallCostMultiplier(state: GameState): number {
  return state.run.doctrine?.id === 'architect' ? 0.75 : 1;
}

export function doctrineToolCostMultiplier(state: GameState): number {
  return state.run.doctrine?.id === 'merchant' ? 0.8 : 1;
}

export function doctrineSaleMultiplier(state: GameState): number {
  if (state.run.doctrine?.id === 'merchant') return 1.4;
  if (state.run.doctrine?.id === 'architect') return 0.85;
  return 1;
}

export function doctrineOfflineHours(state: GameState): number {
  return state.run.doctrine?.id === 'wayfarer' ? 4 : 0;
}

export function doctrineOfflineEfficiencyBonus(state: GameState): number {
  return state.run.doctrine?.id === 'wayfarer' ? 0.15 : 0;
}
